import { BlurEvent, ChangeEvent, DataSource, LookupModelSelectionEvent, TableRow, Textbox } from "@mcleod/components";
import { Api, Currency, CurrencySettings, ModelRow, StringUtil } from "@mcleod/core";
import { OrderRates } from "./OrderRates";
import { QuoteRates } from "./QuoteRates";
import { RecOrderRates } from "./RecOrderRates";
import { BillingRates } from "@mcleod/ar/src/BillingRates"
import { EdiBillingRates } from "@mcleod/datafusion/src/EdiBillingRates";

interface OtherChargeData {
    otherChargeRow: ModelRow;
    otherChargeTextbox: Textbox;
    masterTableId?: string;
}

type RatesLayout = OrderRates | RecOrderRates | QuoteRates | BillingRates | EdiBillingRates;

export class OtherChargesUtil {
    private ratesLayout: RatesLayout;
    private ordersDatasource: DataSource;
    private _currencyType: string;

    public initialize(ratesLayout: RatesLayout) {
        this.ratesLayout = ratesLayout;
        this.ordersDatasource = ratesLayout.mainDataSource;
    }

    async otherChargeOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            const textbox: Textbox = event.target as Textbox;
            await this.calculateOtherCharges({
                otherChargeRow: textbox.boundRow,
                otherChargeTextbox: textbox
            });
        }
    }

    async chargeIdChanged(textbox: Textbox, masterTableId?: string) {
        const otherChargeRow = textbox.boundRow;
        await this.calculateOtherCharges({
            otherChargeRow,
            otherChargeTextbox: textbox,
            masterTableId
        }, true);
    }

    async calculateOtherCharges(props: OtherChargeData, newSelection: boolean = false) {
        const orderInfo = {
            "order_distance": this.ratesLayout instanceof BillingRates ? this.ordersDatasource.activeRow.get("distance", null) : this.ordersDatasource.activeRow.get("bill_distance", null),
            "order_weight": this.ordersDatasource.activeRow.get("weight", null),
            "type": this.getOrderType()
        };
        if (this.ordersDatasource.activeRow.data["freight_charge"] != null) {
            this.addCurrencyToProps(orderInfo, this.ordersDatasource.activeRow, "freight_charge", "order_freight");
        } else if (this.ordersDatasource.activeRow.data["linehaul_chg"] != null && this.ratesLayout instanceof BillingRates) {
            this.addCurrencyToProps(orderInfo, this.ordersDatasource.activeRow, "linehaul_chg", "order_freight");
        }
        if (this.ratesLayout instanceof BillingRates) {
            if (await this.getOtherChargeBillTotal(false) != null) this.addCurrencyValueToProps(orderInfo, "previous_other_charge_total", await this.getOtherChargeBillTotal(false));
        } else {
            if (await this.getOtherChargeTotal(false) != null) this.addCurrencyValueToProps(orderInfo, "previous_other_charge_total", await this.getOtherChargeTotal(false));
        }

        Api.search("lme/dispatch/calculate-other-charge", {
            "other_charge_row": props.otherChargeRow,
            "order_info": orderInfo,
            "new_selection": newSelection,
            "master_table_id": props.masterTableId
        }).then(response => {
            this.otherChargeFromApi(response, props.otherChargeTextbox);
        });
    }

    async textboxChargeIdOnLookupModelSelection(event: LookupModelSelectionEvent, masterTableId?: string) {
        if (event.hasSelection() === true) {
            const textboxDescr: Textbox = TableRow.getContainingTableRow(event.target)?.findComponentById("textboxDescr") as Textbox;
            if (textboxDescr != null)
                textboxDescr.text = event.selection.get("descr");
            await this.chargeIdChanged(event.target, masterTableId);
        }
    }

    textboxStopNumberOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const tableRow: TableRow = TableRow.getContainingTableRow(event.target);
            const modelRow: ModelRow = tableRow?.data;
            const textBoxStopDescription: Textbox = TableRow.getContainingTableRow(event.target)?.findComponentById("textboxStopIdDescription") as Textbox;
            if (textBoxStopDescription != null) {
                textBoxStopDescription.text = event.selection.get("location_name");
                modelRow.set("stop_loc_name", event.selection.get("location_name"));
            }
            modelRow.set("stop_id", event.selection.get("id"));
            modelRow.set("stop_no", event.selection.get("order_sequence"));
        }
    }

    textboxStopNumberOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange && StringUtil.isEmptyString(event.newValue)) {
            const textbox: Textbox = event.target as Textbox;
            const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
            const modelRow: ModelRow = tableRow?.data;
            modelRow.set("stop_id", null);
            modelRow.set("stop_loc_name", null);
        }
    }

    async tableOtherChargesOnContentsChanged() {
        this.ordersDatasource.activeRow.set("otherchargetotal", await this.getOtherChargeTotal(false));
        this.ordersDatasource.activeRow.set("total_charge", await this.getOtherChargeTotal(true));
    }

    async tableOtherChargeBillOnContentsChanged() {
        // Ensure calculations are done in proper sequence to avoid race conditions
        const otherChargeTotal = await this.getOtherChargeBillTotal(false);
        const totalCharges = await this.getOtherChargeBillTotal(true);

        // Update both values atomically to prevent inconsistent state
        this.ordersDatasource.activeRow.set("other_charge", otherChargeTotal);
        this.ordersDatasource.activeRow.set("total_charges", totalCharges);
    }

    getOrderType(): string {
        if (this.ratesLayout instanceof OrderRates) {
            return "ORDER";
        } else if (this.ratesLayout instanceof RecOrderRates) {
            return "RECURRING";
        } else if (this.ratesLayout instanceof BillingRates || this.ratesLayout instanceof EdiBillingRates) {
            return "BILLING"
        } else {
            return "QUOTE"
        }
    }

    addCurrencyToProps(props, row: ModelRow, fieldName: string, propName: string) {
        const currencyValue = row.get(fieldName);
        this.addCurrencyValueToProps(props, propName, currencyValue);
    }

    addCurrencyValueToProps(props: any, propName: string, currencyValue: any) {
        props[propName] = currencyValue;
    }

    async getOtherChargeTotal(includeFreightCharge: boolean) {
        const activeRow = this.ordersDatasource.activeRow;
        const freightCharge = includeFreightCharge ? activeRow.get("freight_charge") : null;
        const conversionDate: Date = activeRow.getCurrency("freight_charge", new Currency(0.0)).getConversionDate();
        if (this.currencyType) {
            return await OtherChargesUtil.calculateOtherChargeTotal(this.ratesLayout.getOtherChargeDatasource(),
                new Currency({
                    amount: freightCharge?.amount ?? 0,
                    currency_code: this.currencyType,
                    conversion_date: conversionDate
                }));
        }
        return await OtherChargesUtil.calculateOtherChargeTotal(this.ratesLayout.getOtherChargeDatasource(), freightCharge);
    }

    async getOtherChargeBillTotal(includeFreightCharge: boolean) {
        const activeRow = this.ordersDatasource.activeRow;
        const freightCharge = includeFreightCharge ? activeRow.get("linehaul_chg") : null;
        const conversionDate: Date = activeRow.getCurrency("linehaul_chg", new Currency(0.0)).getConversionDate();
        if (this.currencyType) {
            return await OtherChargesUtil.calculateOtherChargeTotal(this.ratesLayout.getOtherChargeDatasource(),
                new Currency({
                    amount: freightCharge?.amount ?? 0,
                    currency_code: this.currencyType,
                    conversion_date: conversionDate
                }));
        }
        return await OtherChargesUtil.calculateOtherChargeTotal(this.ratesLayout.getOtherChargeDatasource(), freightCharge);
    }

    public static async calculateOtherChargeTotal(otherChargesDataSource: DataSource, freightCharge?: Currency) {
        const totalAmount: number = freightCharge?.amount ?? 0;
        const currencyCode = freightCharge?.currency_code ?? CurrencySettings.getSingleton().getTransactionalCurrency();
        const conversionDate = freightCharge?.conversion_date ?? new Date();

        const totalOtherCharges = new Currency({
            amount: totalAmount,
            currency_code: currencyCode,
            conversion_date: conversionDate
        });

        // Enhanced debugging to track data source issues
        const chargeCount = otherChargesDataSource.data?.length || 0;
        console.debug(`Calculating other charge total - Initial freight charge: ${totalAmount}, Currency: ${currencyCode}, Other charges count: ${chargeCount}`);

        // Validate data source consistency
        if (!otherChargesDataSource.data || otherChargesDataSource.data.length === 0) {
            console.debug("No other charges found in data source");
            return totalOtherCharges;
        }

        let processedCharges = 0;
        for (const row of otherChargesDataSource.data) {
            const currency = row?.getCurrency("amount");
            if (currency) {
                console.debug(`Adding other charge ${processedCharges + 1}: ${currency.amount} ${currency.currency_code} (ID: ${row.get("id") || 'new'})`);
                await totalOtherCharges.add(currency, false);
                processedCharges++;
            } else {
                console.warn(`Skipping other charge row with null/invalid amount: ${JSON.stringify(row.data)}`);
            }
        }

        console.debug(`Final total charges: ${totalOtherCharges.amount} ${totalOtherCharges.currency_code} (processed ${processedCharges}/${chargeCount} charges)`);
        return totalOtherCharges;
    }

    otherChargeFromApi(response, textbox: Textbox) {
        const data = response.data[0];
        const otherCharge = data["other_charge_row"];
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
        const modelRow = textbox.boundRow;
        modelRow.setValues(otherCharge);
        tableRow.displayComponentValues();

    }

    set currencyType(currencyType: string) {
        this._currencyType = currencyType;
    }

    get currencyType(): string {
        return this._currencyType;
    }
}
